import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { AirQualityService, SensorDataResponse } from "../../services/airQualityService";
import { getLocationsApi, getLocationDetailApi } from "../../lib/api/location";
import { getDeviceListApi } from "../../lib/api/device";
import { useSearchParams } from "react-router-dom";
import "./Stat.css";

const StatView = () => {
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('deviceId');
  const deviceName = searchParams.get('deviceName');

  // 차트 데이터
  const [chartData, setChartData] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState("복합악취");
  // 기본 날짜를 어제~오늘로 설정
  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };
  
  const getToday = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(getYesterday());
  const [endDate, setEndDate] = useState(getToday());
  const [loading, setLoading] = useState(false);
  const [sensorData, setSensorData] = useState<SensorDataResponse[]>([]);

  const airQualityService = AirQualityService.getInstance();

  // 실제 데이터 로드 - 외부 API 사용
  const loadSensorData = async () => {
    if (!deviceId) return;
    
    try {
      setLoading(true);
      console.log(`Loading sensor data for device ${deviceId} from ${startDate} to ${endDate}`);
      // 외부 API 호출로 변경 (deviceId를 문자열로 사용)
      const data = await airQualityService.getDeviceSensorDataByString(
        deviceId, 
        startDate, 
        endDate
      );
      console.log(`Received ${data.length} sensor data records:`, data);
      setSensorData(data);
      generateChartDataFromReal(data);
    } catch (error) {
      console.error("Failed to load sensor data:", error);
      setChartData([]); // 에러 시에도 빈 차트 표시
      setSensorData([]);
    } finally {
      setLoading(false);
    }
  };

  // 실제 데이터로부터 차트 데이터 생성 (날짜 범위에 따른 동적 그룹화)
  const generateChartDataFromReal = (data: SensorDataResponse[]) => {
    if (!data || data.length === 0) {
      setChartData([]); // 데이터가 없으면 빈 차트 표시
      return;
    }

    // 날짜 범위 계산 (일 단위)
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    // 날짜 범위에 따라 그룹화 방식 결정
    let groupingKey: (time: Date) => string;
    let timeFormatter: (time: Date) => string;

    if (daysDiff <= 1) {
      // 1일 이하: 시간별 (HH:MM)
      groupingKey = (time) => `${time.getHours().toString().padStart(2, "0")}:${time.getMinutes().toString().padStart(2, "0")}`;
      timeFormatter = (time) => `${time.getHours().toString().padStart(2, "0")}:${time.getMinutes().toString().padStart(2, "0")}`;
    } else if (daysDiff <= 7) {
      // 7일 이하: 일별 (MM/DD)
      groupingKey = (time) => `${(time.getMonth() + 1).toString().padStart(2, "0")}/${time.getDate().toString().padStart(2, "0")}`;
      timeFormatter = (time) => `${(time.getMonth() + 1).toString().padStart(2, "0")}/${time.getDate().toString().padStart(2, "0")}`;
    } else if (daysDiff <= 31) {
      // 31일 이하: 일별 (MM/DD)
      groupingKey = (time) => `${(time.getMonth() + 1).toString().padStart(2, "0")}/${time.getDate().toString().padStart(2, "0")}`;
      timeFormatter = (time) => `${(time.getMonth() + 1).toString().padStart(2, "0")}/${time.getDate().toString().padStart(2, "0")}`;
    } else {
      // 31일 초과: 주별 그룹화
      groupingKey = (time) => {
        const weekStart = new Date(time);
        weekStart.setDate(time.getDate() - time.getDay());
        return `${(weekStart.getMonth() + 1).toString().padStart(2, "0")}/${weekStart.getDate().toString().padStart(2, "0")}`;
      };
      timeFormatter = (time) => {
        const weekStart = new Date(time);
        weekStart.setDate(time.getDate() - time.getDay());
        return `${(weekStart.getMonth() + 1).toString().padStart(2, "0")}/${weekStart.getDate().toString().padStart(2, "0")}`;
      };
    }

    // 데이터 그룹화
    const groupedData = new Map();
    
    data.forEach((item) => {
      const time = new Date(item.measuredAt || item.time);
      const key = groupingKey(time);
      
      let value;
      switch (selectedMetric) {
        case "복합악취":
          value = 0; // 복합악취는 외부 API에서 추후 제공 예정으로 현재는 0으로 표기
          break;
        case "황화수소":
          value = item.h2s || 0;
          break;
        case "암모니아":
          value = item.nh3 || 0;
          break;
        case "메탄가스":
          value = item.ch4 || 0;
          break;
        case "온도":
          value = item.tv || item.temperature || 0;
          break;
        case "습도":
          value = item.hv || item.humidity || 0;
          break;
        case "풍향":
          value = item.wd || item.windDirection || 0;
          break;
        case "풍속":
          value = item.ws || item.windSpeed || 0;
          break;
        default:
          value = 0;
      }

      if (!groupedData.has(key)) {
        groupedData.set(key, {
          time: time,
          values: [],
          count: 0
        });
      }
      
      const group = groupedData.get(key);
      group.values.push(value);
      group.count++;
    });

    // 평균값 계산 및 정렬
    const chartData = Array.from(groupedData.entries())
      .map(([key, group]) => {
        const avgValue = group.values.reduce((sum, val) => sum + val, 0) / group.values.length;
        return {
          time: timeFormatter(group.time),
          value: Number(avgValue.toFixed(3)),
          originalTime: group.time
        };
      })
      .sort((a, b) => a.originalTime.getTime() - b.originalTime.getTime());

    console.log(`Generated ${chartData.length} chart points for ${daysDiff} days range:`, chartData);
    setChartData(chartData);
  };

  // 전체 나주시 데이터 로드 - 외부 API 사용 (deviceId가 없을 때)
  const loadCityData = async () => {
    try {
      setLoading(true);
      
      const allSensorData = [];
      
      // 사업장에 할당된 기기들의 외부 API 센서 데이터만 조회
      try {
        const locationResponse = await getLocationsApi({
          size: 100,
          page: 0
        });
        
        console.log(`Found ${locationResponse.content.length} locations, checking for assigned devices...`);
        
        for (const location of locationResponse.content) {
          try {
            const locationDetail = await getLocationDetailApi(location.id);
            
            if (locationDetail.devices && locationDetail.devices.length > 0) {
              const activeDevices = locationDetail.devices.filter(device => device.status === 'ACTIVE');
              
              console.log(`Location ${locationDetail.locationName} has ${activeDevices.length} active devices`);
              
              for (const device of activeDevices) {
                try {
                  const deviceCode = device.deviceCode;
                  
                  console.log(`Trying to load data for assigned device: ${deviceCode} at ${locationDetail.locationName}`);
                  
                  let sensorData = [];
                  
                  if (deviceCode) {
                    try {
                      // 외부 API 호출로 변경
                      sensorData = await airQualityService.getDeviceSensorDataByString(
                        deviceCode,
                        startDate,
                        endDate
                      );
                      console.log(`Data found for device ${deviceCode}:`, sensorData.length);
                    } catch (error) {
                      console.log(`No data for device ${deviceCode}`);
                    }
                  }
                  
                  if (sensorData && sensorData.length > 0) {
                    allSensorData.push(...sensorData.map(data => ({
                      ...data,
                      locationName: locationDetail.locationName,
                      deviceId: deviceCode,
                      deviceCode: device.deviceCode
                    })));
                  } else {
                    console.log(`No sensor data found for assigned device ${deviceCode}`);
                  }
                } catch (error) {
                  console.error(`Failed to load data for device ${device.deviceCode}:`, error);
                }
              }
            } else {
              console.log(`Location ${locationDetail.locationName} has no assigned devices`);
            }
          } catch (error) {
            console.error(`Failed to load detail for location ${location.id}:`, error);
          }
        }
      } catch (error) {
        console.error('Failed to load location-based devices:', error);
      }
      
      console.log(`Loaded ${allSensorData.length} total sensor readings from all devices`);
      
      setSensorData(allSensorData);
      generateChartDataFromReal(allSensorData);
      
    } catch (error) {
      console.error('Failed to load city data:', error);
      setChartData([]); // 에러 시에도 빈 차트 표시
      setSensorData([]);
    } finally {
      setLoading(false);
    }
  };

  // 데이터 로드 (selectedMetric 제거하여 탭 변경시 API 호출 방지)
  useEffect(() => {
    if (deviceId) {
      loadSensorData();
    } else {
      loadCityData(); // 전체 나주시 데이터 로드
    }
  }, [deviceId, startDate, endDate]);

  // 메트릭 변경시 기존 데이터로 차트 재생성 (API 호출 없음)
  useEffect(() => {
    if (sensorData.length > 0) {
      generateChartDataFromReal(sensorData);
    }
  }, [selectedMetric]);

  const generateChartData = () => {
    const data = [];
    for (let i = 0; i < 24; i++) {
      const hour = i.toString().padStart(2, "0");
      let value;

      switch (selectedMetric) {
        case "복합악취":
          value = 0.2 + Math.random() * 1.3;
          break;
        case "황화수소":
          value = 7.5 + Math.random() * 2;
          break;
        case "암모니아":
          value = 0.5 + Math.random() * 1.5;
          break;
        case "메탄가스":
          value = 100 + Math.random() * 60;
          break;
        case "온도":
          value = 25 + Math.random() * 5;
          break;
        case "습도":
          value = 0;
          break;
        case "풍향":
          value = Math.random() * 360;
          break;
        case "풍속":
          value = 0.5 + Math.random() * 0.5;
          break;
      }

      data.push({
        time: `${hour}시`,
        value: value,
      });
    }
    setChartData(data);
  };

  const handleExcelDownload = () => {
    console.log("엑셀 다운로드");
    // 실제 구현 시 downloadDevicesExcelApi() 호출
  };

  // CustomTooltip 컴포넌트에 타입 추가
  interface CustomTooltipProps {
    active?: boolean;
    payload?: any[];
    label?: string;
  }

// 차트 커스텀 툴팁
  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
          <div className="chart-tooltip">
            <p style={{ margin: 0, whiteSpace: 'pre-line', color: 'white' }}>
              {`${label}\n값: ${payload[0].value.toFixed(2)}`}
            </p>
          </div>
      );
    }
    return null;
  };

  return (
    <div className="device-list-container">
      {/* 탭 버튼 - DeviceListView와 동일한 구조 */}
      <div className="tab-buttons">
        <div className="" style={{ float: "left" }}>
          <button
            className="tab-button active"
            onClick={() => {
              window.location.href = "/stat";
            }}
          >
{deviceName ? `${deviceName} 시간대별 종합수치` : "나주시 시간대별 종합수치"}
          </button>
          <button
            className="tab-button"
            onClick={() => {
              window.location.href = "/stat/region";
            }}
          >
            지역 시간대별 종합수치
          </button>
          <button
            className="tab-button"
            onClick={() => {
              window.location.href = "/stat/location";
            }}
          >
            사업장 시간대별 종합수치
          </button>
        </div>
        <a
          href={"javascript:;"}
          className="btn-excel-download"
          onClick={handleExcelDownload}
          style={{ float: "right" }}
        >
          <img src={"/img/btn_excel01.png"} />
        </a>
      </div>

      {/* dlv_dv01 박스 - DeviceListView와 동일한 구조 */}
      <div className="dlv_dv01">
        <div className="env-content">
          <div className="date-selector">
            <span>조회일</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="date-input"
            />
            <span className="date-separator">~</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="date-input"
            />
          </div>

          <div className="table-section">
            <table className="device-table">
              <thead>
                <tr>
                  <th>측정일자</th>
                  <th>측정시간</th>
                  <th>복합악취(배수)</th>
                  <th>황화수소(ppm)</th>
                  <th>암모니아(ppm)</th>
                  <th>메탄가스(ppm)</th>
                  <th>온도</th>
                  <th>습도</th>
                  <th>풍향</th>
                  <th>풍속</th>
                  <th>등급</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={11}>데이터 로딩 중...</td>
                  </tr>
                ) : sensorData.length > 0 ? (
                  sensorData.map((data, i) => {
                    const date = new Date(data.measuredAt);
                    return (
                      <tr key={i}>
                        <td>{date.toLocaleDateString('ko-KR')}</td>
                        <td>{date.getHours().toString().padStart(2, '0')}시</td>
                        <td>0</td>
                        <td>{(data.h2s || 0).toFixed(2)}</td>
                        <td>{(data.nh3 || 0).toFixed(2)}</td>
                        <td>{(data.ch4 || 0).toFixed(2)}</td>
                        <td>{(data.temperature || 0).toFixed(1)}°C</td>
                        <td>{(data.humidity || 0).toFixed(1)}%</td>
                        <td>{data.windDirection === 0 ? '-' : `${data.windDirection}°`}</td>
                        <td>{data.windSpeed === 0 ? '-' : `${(data.windSpeed || 0).toFixed(2)}m/s`}</td>
                        <td>{data.compositeGrade || "좋음"}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={11}>표시할 데이터가 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="metric-buttons">
            {["복합악취", "황화수소", "암모니아", "메탄가스", "온도", "습도", "풍향", "풍속"].map(
              (metric) => (
                <button
                  key={metric}
                  className={`metric-btn ${selectedMetric === metric ? "active" : ""}`}
                  onClick={() => setSelectedMetric(metric)}
                >
                  {metric}
                </button>
              )
            )}
          </div>

          <div className="chart-container">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="time" stroke="#666" tick={{ fill: "#999", fontSize: 12 }} />
                  <YAxis
                    stroke="#666"
                    tick={{ fill: "#999", fontSize: 12 }}
                    domain={[0, "dataMax + 1"]}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#1dd3d6"
                    strokeWidth={2}
                    dot={{ fill: "#1dd3d6", r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ 
                height: 300, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                color: '#666',
                fontSize: '16px'
              }}>
                표시할 데이터가 없습니다.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatView;
